import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { greenColor } from '../../../config/colors';
import { useTranslation } from '../../../hooks/useTranslation';
import { EMPTY_CONTRACT_FILTERS } from '../../../constants/contractFilters';

const FieldLabel = ({ children }) => (
  <Typography
    component="label"
    sx={{ fontSize: '13px', fontWeight: 600, color: '#424242', mb: 0.5, display: 'block' }}
  >
    {children}
  </Typography>
);

const FilterSelect = ({ label, value, onChange, options, placeholder, id }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <FormControl fullWidth size="small">
      <InputLabel id={`${id}-label`} shrink={false} sx={{ display: value ? 'none' : 'block' }}>
        {placeholder}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        inputProps={{ 'aria-label': label }}
        sx={{ borderRadius: '8px', bgcolor: '#fff' }}
      >
        <MenuItem value="">
          <em style={{ color: '#9e9e9e', fontStyle: 'normal' }}>{placeholder}</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);


const ContractFilterModal = ({ open, onClose, onApply, initialFilters, options }) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState(EMPTY_CONTRACT_FILTERS);

  useEffect(() => {
    if (!open) return;
    setFilters(initialFilters ?? EMPTY_CONTRACT_FILTERS);
  }, [open, initialFilters]);

  const handleField = (field) => (value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleClear = () => {
    setFilters(EMPTY_CONTRACT_FILTERS);
    onApply(EMPTY_CONTRACT_FILTERS);
    onClose();
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="contract-filter-modal-title"
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      {/* Título */}
      <DialogTitle
        id="contract-filter-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
          fontSize: '16px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {t('contracts.filterModal.title')}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('common.close')}
          sx={{ color: '#9e9e9e' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Campos */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '16px !important',
          overflowY: 'auto',
        }}
      >
        {/* Cliente autocomplete com busca */}
        <Box>
          <FieldLabel>{t('contracts.filterModal.fields.client')}</FieldLabel>
          <Autocomplete
            options={options.clients}
            getOptionLabel={(opt) => opt.name}
            value={filters.client}
            onChange={(_, newValue) => handleField('client')(newValue)}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            size="small"
            noOptionsText={t('contracts.filterModal.noOptions')}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('contracts.filterModal.placeholderSelect')}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': t('contracts.filterModal.fields.client'),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            )}
          />
        </Box>

        {/* Empresa */}
        <FilterSelect
          id="filter-company"
          label={t('contracts.filterModal.fields.company')}
          placeholder={t('contracts.filterModal.placeholderSelect')}
          value={filters.company}
          onChange={handleField('company')}
          options={options.companies.map((c) => ({ value: c.id, label: c.name }))}
        />

        {/* Situação */}
        <FilterSelect
          id="filter-situation"
          label={t('contracts.filterModal.fields.situation')}
          placeholder={t('contracts.filterModal.placeholderSelect')}
          value={filters.situation}
          onChange={handleField('situation')}
          options={options.situations}
        />

        {/* Status */}
        <FilterSelect
          id="filter-status"
          label={t('contracts.filterModal.fields.status')}
          placeholder={t('contracts.filterModal.placeholderSelect')}
          value={filters.status}
          onChange={handleField('status')}
          options={options.statuses}
        />

        {/* Período */}
        <Box>
          <FieldLabel>{t('contracts.filterModal.fields.period')}</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={filters.dateStart}
              onChange={(e) => handleField('dateStart')(e.target.value)}
              inputProps={{
                'aria-label': t('contracts.filterModal.fields.dateStart'),
                max: filters.dateEnd || undefined,
              }}
              placeholder={t('contracts.filterModal.fields.dateStart')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              value={filters.dateEnd}
              onChange={(e) => handleField('dateEnd')(e.target.value)}
              inputProps={{
                'aria-label': t('contracts.filterModal.fields.dateEnd'),
                min: filters.dateStart || undefined,
              }}
              placeholder={t('contracts.filterModal.fields.dateEnd')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Box>
        </Box>

        {/* Tipo de Financiamento */}
        <FilterSelect
          id="filter-financing-type"
          label={t('contracts.filterModal.fields.financingType')}
          placeholder={t('contracts.filterModal.placeholderSelect')}
          value={filters.financingType}
          onChange={handleField('financingType')}
          options={options.financingTypes}
        />

        {/* Modelo */}
        <FilterSelect
          id="filter-model"
          label={t('contracts.filterModal.fields.model')}
          placeholder={t('contracts.filterModal.placeholderSelect')}
          value={filters.model}
          onChange={handleField('model')}
          options={options.models}
        />
      </DialogContent>

      {/* Ações */}
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          gap: 1,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={handleClear}
          sx={{
            borderColor: '#e0e0e0',
            color: '#424242',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' },
          }}
        >
          {t('contracts.filterModal.clearFilters')}
        </Button>

        <Button
          fullWidth
          variant="contained"
          onClick={handleApply}
          sx={{
            bgcolor: greenColor,
            color: '#000',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { bgcolor: '#6ec13b' },
          }}
        >
          {t('contracts.filterModal.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractFilterModal;
